import type { Locale } from '@core/Entities/Locale/Locale.entity';
import { createPathFromFilters } from '@core/Utils/Search/CreatePathFromFilters';
import type { InterlinkingContent } from '@page-builder/Components/InterLinking/Entities/InterLinking.entity';

import type { ExtendedAggregations, SectionConfig } from '../MakeModelInterlinking.entities';
import {
  MAKE_INTERLINKING_SECTIONS,
  MAKE_MODEL_INTERLINKING_SECTIONS,
} from '../MakeModelInterlinkingSectionsConfigs';

type SectionOption = {
  id: string;
  count: number;
  label: string;
  labelKey?: string;
};
export const getAllOptions = (options: SectionOption[]): SectionOption[] => {
  return options.filter(option => option.count > 0).sort((a, b) => b.count - a.count);
};

export const getMessageDictionary = (
  config: SectionConfig,
  bodyTypeMessages: Record<string, string>,
  fuelTypeMessages: Record<string, string>,
  gearBoxMessages: Record<string, string>,
  colorMessages: Record<string, string>,
): Record<string, string> => {
  switch (config.messageKey) {
    case 'body-types':
      return bodyTypeMessages;
    case 'fuel-types':
      return fuelTypeMessages;
    case 'transmissions':
      return gearBoxMessages;
    case 'colors':
      return colorMessages;
    default:
      return {};
  }
};

const createLinkBlock = (
  config: SectionConfig,
  options: SectionOption[],
  messages: Record<string, string>,
  make: string,
  modelWithMake: string | undefined,
  locale: Locale,
  makeDisplayName: string,
  modelDisplayName: string | undefined,
): InterlinkingContent['interlinkingItems'][number]['linkBlocks'][number] | null => {
  const allOptions = getAllOptions(options);
  if (allOptions.length === 0) return null;

  const cta = allOptions.map(option => {
    const displayName = config.getDisplayName(option, messages);
    const filters = config.createFilters(make, modelWithMake, option.id);
    return {
      ctaText: config.formatCtaText(makeDisplayName, modelDisplayName, displayName),
      link: createPathFromFilters(filters, locale),
    };
  });

  return {
    heading: messages[config.headingKey],
    // Temporary fix to avoid breaking changes to the PLP.spec tests
    dataTestId: config.key === 'models' ? 'plp-all-models' : `${config.key}-interlinking-section`,
    cta,
  };
};

export const transformToInterlinkingContent = (
  aggregations: ExtendedAggregations,
  make: string,
  modelWithMake: string | undefined,
  locale: Locale,
  messages: Record<string, string>,
  makeDisplayName: string,
  modelDisplayName: string | undefined,
): InterlinkingContent => {
  const configs = modelWithMake ? MAKE_MODEL_INTERLINKING_SECTIONS : MAKE_INTERLINKING_SECTIONS;

  const linkBlocks: InterlinkingContent['interlinkingItems'][number]['linkBlocks'] = configs
    .map(config =>
      createLinkBlock(
        config,
        config.getOptions(aggregations, make, modelWithMake),
        messages,
        make,
        modelWithMake,
        locale,
        makeDisplayName,
        modelDisplayName,
      ),
    )
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return {
    variant: [
      {
        name: 'collapsible',
        codename: 'collapsible',
      },
    ],
    interlinkingItems: [
      {
        heading: messages.moreOptions,
        linkBlocks: linkBlocks,
      },
    ],
  };
};
